import React, { useEffect, useRef, useState } from "react";
import { api, API, authedFetch } from "../lib/api";
import { compressImage } from "../lib/imageCompression";
import useFileDropzone from "../lib/useFileDropzone";
import { FileText, FilePlus2, X, Loader2, ImageIcon, ExternalLink, FileBox, Download } from "lucide-react";
import { openLightbox } from "./Lightbox";

/**
 * DocumentUploader
 * Uploads images, PDFs, or CAD files (DWG/DXF) via /api/upload/document.
 * @param value: array of { path, content_type, original_name } or plain path strings
 * @param onChange: (newArr) => void
 * @param max: max files
 * @param readOnly: only render, no upload/remove
 * @param allowCad: include .dwg / .dxf as accepted file types (default false)
 */
export default function DocumentUploader({
  value = [], onChange, max = 20, readOnly = false, allowCad = false,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [urls, setUrls] = useState({}); // path -> blob url (images only)

  const items = (value || []).map((v) =>
    typeof v === "string"
      ? { path: v, content_type: null, original_name: null }
      : { path: v.path, content_type: v.content_type || null, original_name: v.original_name || null }
  );

  const isImage = (ct, path) => {
    if (ct && ct.startsWith("image/")) return true;
    const p = (path || "").toLowerCase();
    return /\.(png|jpg|jpeg|webp|heic|gif)$/.test(p);
  };
  const isCad = (ct, path) => {
    const p = (path || "").toLowerCase();
    return /\.(dwg|dxf)$/.test(p);
  };

  // Load image previews
  useEffect(() => {
    let cancelled = false;
    const blobs = {};
    (async () => {
      for (const it of items) {
        if (!isImage(it.content_type, it.path)) continue;
        if (urls[it.path]) continue;
        try {
          const res = await authedFetch(`${API}/files/${it.path}`);
          if (!res.ok) continue;
          const blob = await res.blob();
          const u = URL.createObjectURL(blob);
          if (cancelled) URL.revokeObjectURL(u);
          else blobs[it.path] = u;
        } catch { /* ignore */ }
      }
      if (!cancelled && Object.keys(blobs).length) {
        setUrls((p) => ({ ...p, ...blobs }));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.map((v) => (typeof v === "string" ? v : v.path)).join(",")]);

  useEffect(() => () => {
    Object.values(urls).forEach((u) => { try { URL.revokeObjectURL(u); } catch { /* */ } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pick = () => inputRef.current?.click();

  const uploadOne = async (file) => {
    setError("");
    setUploading(true);
    try {
      const payload = file.type && file.type.startsWith("image/") ? await compressImage(file) : file;
      const fd = new FormData();
      fd.append("file", payload);
      const { data } = await api.post("/upload/document", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      return data;
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Upload fallito");
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleFiles = async (files) => {
    let added = [];
    let remaining = max - items.length;
    for (const f of files) {
      if (remaining <= 0) break;
      // Reject DWG/DXF unless allowCad (UI-side; backend will also enforce)
      const lname = (f.name || "").toLowerCase();
      if (!allowCad && /\.(dwg|dxf)$/.test(lname)) {
        setError("File DWG/DXF non supportato in questa sezione");
        continue;
      }
      const data = await uploadOne(f);
      if (data) {
        added.push({
          path: data.path, content_type: data.content_type, original_name: data.original_name,
        });
        remaining -= 1;
      }
    }
    if (added.length) onChange([...value, ...added]);
  };

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    await handleFiles(files);
  };

  const remove = (path) => {
    onChange(value.filter((v) => (typeof v === "string" ? v : v.path) !== path));
  };

  const acceptList = ["png", "jpg", "jpeg", "webp", "heic", "gif", "pdf", ...(allowCad ? ["dwg", "dxf"] : [])];
  const acceptStr = `image/*,application/pdf${allowCad ? ",.dwg,.dxf" : ""}`;
  const { handlers: dropHandlers, isOver, isDesktop } = useFileDropzone({
    onFiles: handleFiles,
    accept: acceptList,
    multiple: true,
    disabled: readOnly || uploading || items.length >= max,
  });

  return (
    <div data-testid="document-uploader">
      <div
        {...dropHandlers}
        className={`flex gap-3 flex-wrap transition-all ${
          isOver
            ? "ring-2 ring-[#ff5a00] ring-offset-2 bg-[#fff7f0] rounded-xl p-2 -m-2"
            : ""
        }`}
      >
        {items.map((it) => {
          const img = isImage(it.content_type, it.path);
          const cad = isCad(it.content_type, it.path);
          const shortName =
            it.original_name ||
            (it.path.split("/").pop() || "file").split(".").slice(0, -1).join(".") ||
            "file";
          const ext = ((it.path.split(".").pop() || "").toUpperCase()).slice(0, 4);
          return (
            <div
              key={it.path}
              className="relative w-28 h-28 border-[1.5px] border-black bg-neutral-100 overflow-hidden bru-shadow-sm rounded-lg"
            >
              {img ? (
                urls[it.path] ? (
                  <button
                    type="button"
                    onClick={() => openLightbox(it.path, shortName)}
                    className="w-full h-full"
                    data-testid="doc-open-image"
                    title="Apri immagine"
                  >
                    <img src={urls[it.path]} alt={shortName} className="w-full h-full object-cover cursor-zoom-in" />
                  </button>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Loader2 className="w-5 h-5 animate-spin text-neutral-500" />
                  </div>
                )
              ) : cad ? (
                <a
                  href={`${API}/public/files/${it.path}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={it.original_name || (it.path.split("/").pop() || "file")}
                  className="w-full h-full flex flex-col items-center justify-center gap-1 bg-white hover:bg-[#fff7f0] px-2"
                  data-testid="doc-download-cad"
                  title={`${shortName} · Scarica ${ext}`}
                >
                  <FileBox className="w-8 h-8 text-[#ff5a00]" />
                  <div className="text-[10px] font-bold uppercase tracking-wider truncate max-w-full">{`Scarica ${ext}`}</div>
                  <div className="text-[10px] text-neutral-600 truncate max-w-full">{shortName}</div>
                  <Download className="w-3 h-3 text-neutral-500 absolute bottom-1 right-1" />
                </a>
              ) : (
                <a
                  href={`${API}/public/files/${it.path}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full h-full flex flex-col items-center justify-center gap-1 bg-white hover:bg-[#fff7f0] px-2"
                  data-testid="doc-open-pdf"
                  title={`${shortName} · Apri`}
                >
                  <FileText className="w-8 h-8 text-[#ff5a00]" />
                  <div className="text-[10px] font-bold uppercase tracking-wider truncate max-w-full">PDF</div>
                  <div className="text-[10px] text-neutral-600 truncate max-w-full">{shortName}</div>
                  <ExternalLink className="w-3 h-3 text-neutral-500 absolute bottom-1 right-1" />
                </a>
              )}
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => remove(it.path)}
                  className="absolute top-1 right-1 bg-black text-white w-6 h-6 flex items-center justify-center hover:bg-[#ff3b30] rounded-full"
                  data-testid="doc-remove-btn"
                  aria-label="Rimuovi"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        })}
        {!readOnly && items.length < max && (
          <button
            type="button"
            onClick={pick}
            disabled={uploading}
            className={`w-28 h-28 border-[1.5px] border-dashed flex flex-col items-center justify-center gap-1 disabled:opacity-60 rounded-lg transition-colors ${
              isOver ? "border-[#ff5a00] bg-[#fff7f0]" : "border-black bg-white hover:bg-[#fff7f0]"
            }`}
            data-testid="doc-add-btn"
          >
            {uploading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <FilePlus2 className="w-6 h-6" />
                <span className="text-[10px] uppercase font-bold tracking-wider">{isOver ? "Rilascia" : "Aggiungi"}</span>
                <span className="text-[9px] text-neutral-500 flex items-center gap-0.5">
                  <ImageIcon className="w-3 h-3" /> / PDF{allowCad ? " / DWG" : ""}
                </span>
              </>
            )}
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={acceptStr}
        multiple
        onChange={onFile}
        className="hidden"
        data-testid="doc-file-input"
      />
      {!readOnly && (
        <div className="text-xs text-neutral-500 mt-2">
          Immagini, PDF{allowCad ? ", o file CAD (DWG/DXF)" : ""} · max {max} file · max 20 MB per file
          {isDesktop && " · trascina i file qui per caricarli"}
        </div>
      )}
      {error && <div className="text-xs text-[#ff3b30] mt-1" data-testid="doc-uploader-error">{error}</div>}
    </div>
  );
}
